import { useCallback, useMemo } from 'react';

import {
    Text,
    Cell,
    CellText,
    Radio,
    VSpacing,
    HSpacing,
    createDataProvider,
    createStaticDataFetcher,
    DataSection,
    DataSectionOfType,
    decorateWithResultProducer,
    findStaticDataFetcherItemByValue,
    StaticDataFetcherItem,
} from '@hh.ru/magritte-ui';
import { RenderOptionType, Select } from '@hh.ru/magritte-ui-select';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { CriteriaKey } from 'src/types/search/common/criteria';
import { DEFAULT_LANGUAGES } from 'src/utils/sortForLanguages';

const TrlKeys = {
    placeholder: 'search.filter.language.placeholder',
    popularDelimiter: 'search.filter.language.delimiter.popular',
};

interface LanguageSelectProps {
    code: string;
    onChange: (code: string) => void;
    selectedLanguages: string[];
    dataQa: string;
}

const LanguageSelect: TranslatedComponent<LanguageSelectProps> = ({
    code,
    onChange,
    selectedLanguages,
    dataQa,
    trls,
}) => {
    const languages = useSelector((state) => state.languages.language);

    const items = useMemo(() => languages.map((item) => ({ value: item.code, text: item.title })), [languages]);

    const provider = useMemo(() => {
        return createDataProvider({
            minCharsCount: 0,
            debounceTimeout: 0,
            fetcher: decorateWithResultProducer(createStaticDataFetcher(items), (fetcherData) => {
                const sections: DataSection<StaticDataFetcherItem>[] = [];
                const popularSection: DataSectionOfType<'cells', StaticDataFetcherItem> = { type: 'cells', items: [] };
                let otherSection: DataSectionOfType<'cells', StaticDataFetcherItem> = { type: 'cells', items: [] };
                let currentLetterDelimiter = '';

                sections.push({ type: 'delimiter', text: trls[TrlKeys.popularDelimiter] });
                sections.push(popularSection);

                fetcherData?.forEach((item) => {
                    if (DEFAULT_LANGUAGES.includes(item.value)) {
                        popularSection.items.push({ value: item.value, data: item });
                    } else {
                        const firstLetter = item.text.charAt(0).toUpperCase();
                        if (firstLetter !== currentLetterDelimiter) {
                            if (otherSection.items.length) {
                                sections.push(otherSection);
                                otherSection = { type: 'cells', items: [] };
                            }
                            sections.push({ type: 'delimiter', text: firstLetter });
                            currentLetterDelimiter = firstLetter;
                        }
                        otherSection.items.push({ value: item.value, data: item });
                    }
                });

                if (otherSection.items.length) {
                    sections.push(otherSection);
                }

                return sections;
            }),
        });
    }, [items, trls]);

    const renderOption: RenderOptionType<StaticDataFetcherItem> = useCallback(
        ({ data, type }) => {
            if (type === 'delimiter') {
                return (
                    <>
                        <VSpacing default={8} />
                        <HSpacing default={16} />
                        <Text Element="span" typography="label-2-regular" style="secondary">
                            {data.text}
                        </Text>
                    </>
                );
            }
            const isSelected = data.value === code;
            return (
                <Cell
                    left={<Radio checked={isSelected} readOnly />}
                    onChange={() => onChange(data.value)}
                    disabled={!isSelected && selectedLanguages.includes(data.value)}
                >
                    <CellText data-qa={`${dataQa}-${data.value}`}>{data.text}</CellText>
                </Cell>
            );
        },
        [code, dataQa, onChange, selectedLanguages]
    );

    return (
        <div style={{ position: 'relative' }} data-qa={dataQa}>
            <Select
                type="radio"
                name={CriteriaKey.Language}
                dataProvider={provider}
                triggerProps={{
                    size: 'medium',
                    label: trls[TrlKeys.placeholder],
                    stretched: true,
                }}
                renderDesktopItem={renderOption}
                renderMobileItem={renderOption}
                value={findStaticDataFetcherItemByValue(code, items)}
                onChange={(newItem) => onChange(newItem.value)}
            />
        </div>
    );
};

export default translation(LanguageSelect);
